import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  addBranchOfficeToVehicle,
  addDepositToVehicle,
  deleteBranchOfficeFromVehicle,
  deleteDepositFromVehicle,
  editVehicle,
  getBranchOfficesFiltered,
  getDepositsFiltered,
  getVehicleById,
} from "../../../api/Services";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import {
  Form,
  Input,
  Button,
  message,
  Spin,
  Table,
  Space,
  Modal,
  Select,
  InputNumber,
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import styles from "./ViewVehicle.module.scss";

const ViewVehicle = () => {
  const { t } = useTranslation();

  const { id } = useParams();

  const [vehicle, setVehicle] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [isAddDepositModalVisible, setIsAddDepositModalVisible] =
    useState(false);
  const [isAddBranchOfficeModalVisible, setIsAddBranchOfficeModalVisible] =
    useState(false);
  const [branchOffices, setBranchOffices] = useState<any[]>([]);
  const [deposits, setDeposits] = useState<any[]>([]);

  const [editForm] = Form.useForm();
  const [addDepositForm] = Form.useForm();
  const [addBranchOfficeForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_VEHICLES);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_VEHICLES)) {
      handleFetchVehicle();
    }
  }, []);



  const handleFetchVehicle = async () => {
    const response = await getVehicleById(id!);

    setVehicle(response);
    setLoading(false);
  };

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, { name: value });
    setBranchOffices(response.branchOffices);
  };

  const handleSearchDeposits = async (value: string) => {
    const response = await getDepositsFiltered(0, {
      name: value,
    });
    setDeposits(response.deposits);
  };

  const handleEdit = async () => {
    await editForm.validateFields().then(async (values) => {
      setLoading(true);
      const {
        name,
        code,
        brand,
        model,
        licensePlate,
        type,
        maxCapacity,
        fuelConsumption,
      } = values;
      let response = await editVehicle(parseInt(id!), {
        name,
        code,
        brand,
        model,
        licensePlate,
        type,
        maxCapacity,
        fuelConsumption,
      });

      if (response) {
        message.success(t("Common-EditSuccessMessage"));
      } else message.error(t("Common-EditErrorMessage"));

      await handleFetchVehicle();
      setLoading(false);
    });
  };
  const handleCancelButton = () => {
    editForm.resetFields();
  };

  const handleOpenAddDepositModal = () => {
    handleSearchDeposits("");
    setIsAddDepositModalVisible(true);
  };

  const handleCancelAddDepositModal = () => {
    setIsAddDepositModalVisible(false);
  };

  const handleOpenAddBranchOfficeModal = () => {
    handleSearchBranchOffices("");
    setIsAddBranchOfficeModalVisible(true);
  };

  const handleCancelAddBranchOfficeModal = () => {
    setIsAddBranchOfficeModalVisible(false);
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleDeletePlace = async (
    placeId: number,
    place: "branchOffice" | "deposit"
  ) => {
    setLoading(true);

    const deleteStatus =
      place === "branchOffice"
        ? await deleteBranchOfficeFromVehicle(placeId)
        : await deleteDepositFromVehicle(placeId);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));

    await handleFetchVehicle();
    setLoading(false);
  };





  const showDeletePlaceConfirm = (
    placeId: number,
    place: "branchOffice" | "deposit"
  ) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDeletePlace(placeId, place);
      },
    });
  };



  const handleAddDeposit = async () => {
    await addDepositForm.validateFields().then(async (deposit) => {
      setLoading(true);
      const { depositId } = deposit;

      const response = await addDepositToVehicle(parseInt(id!), depositId);

      if (response) {
        addDepositForm.resetFields();
        await handleFetchVehicle();
        handleCancelAddDepositModal();
        message.success(t("Common-AddSuccessMessage"));
      } else message.error(t("Common-ErrorMessage"));
      setLoading(false);
    });
  };



  const handleAddBranchOffice = async () => {
    await addBranchOfficeForm.validateFields().then(async (branchOffice) => {
      setLoading(true);
      const { branchOfficeId } = branchOffice;

      const response = await addBranchOfficeToVehicle(
        parseInt(id!),
        branchOfficeId
      );

      if (response) {
        addBranchOfficeForm.resetFields();
        await handleFetchVehicle();
        handleCancelAddBranchOfficeModal();
        message.success(t("Common-AddSuccessMessage"));
      } else message.error(t("Common-ErrorMessage"));
      setLoading(false);
    });
  };

  const branchOfficesColumns = [
    {
      title: t("Common-Name"),
      key: "name",
      render: (record: any) => (
        <a href={`/admin/branchoffices/${record.branchOffice?.id}/view`}>
          {record.branchOffice?.name}
        </a>
      ),
    },
    {
      title: t("Common-Code"),
      key: "code",
      render: (record: any) => record.branchOffice?.id,
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <DeleteOutlined
            onClick={() => {
              showDeletePlaceConfirm(record.id, "branchOffice");
            }}
          />
        </Space>
      ),
    },
  ];

  const depositsColumns = [
    {
      title: t("Common-Name"),
      key: "name",
      render: (record: any) => (
        <a href={`/admin/deposits/${record.deposit?.id}/view`}>
          {record.deposit?.name}
        </a>
      ),
    },
    {
      title: t("Common-Code"),
      render: (record: any) => record.deposit?.id,
      key: "code",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <DeleteOutlined
            onClick={() => {
              showDeletePlaceConfirm(record.id, "deposit");
            }}
          />
        </Space>
      ),
    },
  ];



  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-Vehicle")}</h1>
        {canEdit && (
          <div className={styles.actionButtonsWrapper}>
            <Button
              onClick={handleEdit}
              className={styles.saveButton}
              type="primary"
            >
              {t("Common-Save")}
            </Button>
            <Button
              onClick={handleCancelButton}
              className={styles.cancelButton}
              type="primary"
            >
              {t("Common-Cancel")}
            </Button>
          </div>
        )}
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.infoWrapper}>
          <Form
            name="vehicle"
            layout="vertical"
            initialValues={{ remember: true }}
            form={editForm}
            autoComplete="off"
            className={styles.form}
            preserve={false}
          >
            <Form.Item
              initialValue={vehicle?.name}
              label={t("Common-Name")}
              name="name"
              className={styles.itemWrapper}
              rules={[
                {
                  required: true,
                  message: t("Common-Name"),
                },
              ]}
            >
              <Input placeholder={t("Common-Name")} />
            </Form.Item>
            <Form.Item
              initialValue={vehicle?.code}
              label={t("Common-Code")}
              name="code"
              className={styles.itemWrapper}
            >
              <Input placeholder={t("Common-Code")} />
            </Form.Item>
            <Form.Item
              initialValue={vehicle?.brand}
              label={t("Common-Brand")}
              name="brand"
            >
              <Input placeholder={t("Common-Brand")} />
            </Form.Item>
            <Form.Item
              initialValue={vehicle?.model}
              label={t("Common-Model")}
              name="model"
            >
              <Input placeholder={t("Common-Model")} />
            </Form.Item>
            <Form.Item
              initialValue={vehicle?.licensePlate}
              label={t("Common-LicensePlate")}
              name="licensePlate"
            >
              <Input placeholder={t("Common-LicensePlate")} />
            </Form.Item>
            <Form.Item
              initialValue={vehicle?.type}
              label={t("Common-Type")}
              name="type"
            >
              <Select allowClear style={{ width: "100%" }} placeholder="">
                <Select.Option value="CAR">{t("Common-Car")}</Select.Option>
                <Select.Option value="VAN">{t("Common-Van")}</Select.Option>
                <Select.Option value="MOTORCYCLE">
                  {t("Common-Motorcycle")}
                </Select.Option>
                <Select.Option value="TRUCK">{t("Common-Truck")}</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              initialValue={vehicle?.maxCapacity}
              label={t("Common-CapacityMaxInUnits")}
              name="maxCapacity"
            >
              <InputNumber
                className={styles.inputNumber}
                placeholder={t("Common-CapacityMaxInUnits")}
              />
            </Form.Item>
            <Form.Item
              label={t("Common-FuelConsumption")}
              name="fuelConsumption"
              initialValue={vehicle?.fuelConsumption}
            >
              <InputNumber
                className={styles.inputNumber}
                placeholder={t("Common-FuelConsumption")}
              />
            </Form.Item>
          </Form>
        </div>


        <div className={styles.tableWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.tablesTitle}>{t("Common-BranchOffices")}</h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={() => handleOpenAddBranchOfficeModal()}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div>
            <Table
              size="small"
              className={styles.table}
              rowKey={(record) => record.id}
              loading={loading}
              columns={branchOfficesColumns}
              dataSource={vehicle?.vehicleBranchOffices?.filter(
                (vehicle: any) => vehicle.branchOffice !== null
              )}
            />
          </div>
        </div>
        <div className={styles.tableWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.tablesTitle}>{t("Common-Deposits")}</h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={() => handleOpenAddDepositModal()}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div>
            <Table
              size="small"
              className={styles.table}
              rowKey={(record) => record.id}
              loading={loading}
              columns={depositsColumns}
              dataSource={vehicle?.vehicleDeposits?.filter(
                (vehicle: any) => vehicle.deposit !== null
              )}
            />
          </div>
        </div>
      </div>

      <Modal
        title={t("Common-Deposit")}
        visible={isAddDepositModalVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={handleCancelAddDepositModal}
      >
        <Form
          name="addDeposit"
          initialValues={{ remember: true }}
          autoComplete="off"
          form={addDepositForm}
        >
          <Form.Item
            className={styles.modalInfoItem}
            label={t("Common-Deposit")}
            name="depositId"
            rules={[
              {
                required: true,
                message: t("Common-Deposit"),
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              filterOption={(input: string, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: "100%" }}
              placeholder={t("Common-SearchByName")}
              onSearch={handleSearchDeposits}
            >
              {getOptions(deposits)}
            </Select>
          </Form.Item>
        </Form>
        <Button
          className={styles.addButton}
          type="primary"
          onClick={handleAddDeposit}
        >
          {t("Common-Add")}
        </Button>
      </Modal>
      <Modal
        title={t("Common-BranchOffice")}
        visible={isAddBranchOfficeModalVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={handleCancelAddBranchOfficeModal}
      >
        <Form
          name="addBranchOffice"
          initialValues={{ remember: true }}
          autoComplete="off"
          form={addBranchOfficeForm}
        >
          <Form.Item
            className={styles.modalInfoItem}
            label={t("Common-BranchOffice")}
            name="branchOfficeId"
            rules={[
              {
                required: true,
                message: t("Common-BranchOffice"),
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              filterOption={(input: string, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: "100%" }}
              placeholder={t("Common-SearchByName")}
              onSearch={handleSearchBranchOffices}
            >
              {getOptions(branchOffices)}
            </Select>
          </Form.Item>
        </Form>
        <Button
          className={styles.addButton}
          type="primary"
          onClick={handleAddBranchOffice}
        >
          {t("Common-Add")}
        </Button>
      </Modal>
    </div>
  );
};
export default ViewVehicle;
